import { captureKey, type PasurMove } from "@bing/engine-pasur";

/* ------------------------------------------------------------------ *
 * THE CAPTURE PICKER — پاسور's own state machine (P1 Task 4, R4 and card-91's refinements), PURE:
 * from the server's `legal`, the lifted hand card, the set the player chose and the frame, to what
 * glows on the table, what is chosen, and what a tap sends. The table (`PasurTable`) wires it to the
 * shared lift (`lift-to-play.ts`): the hook owns the lift, the once-per-frame latch and the one
 * «play» cue; this file owns what a lift MEANS in a fishing game.
 *
 *   ONE legal move  (a trail, or a single capture set) — the pref OFF: a tap plays it at once (the
 *                   card is not liftable, `liftableCards`); the pref ON: a tap lifts it and previews
 *                   its capture, then a tap on the felt (`feltPlays`) or «جمع کن» plays it.
 *   SEVERAL sets    (a 5 with {6} or {4,2}; two matching Queens — duplicates count) — a tap lifts the
 *                   card whatever the pref (the choice is the player's, and a tap cannot make it);
 *                   every table card in any set is ELIGIBLE (a soft glow); a tap on one chooses a
 *                   set holding it, and taps cycle through the sets holding it (`pickOnPool`); the
 *                   CHOSEN set rings and wears a count badge; «جمع کن» plays it — and, with the
 *                   pref OFF, so does a second tap on the lifted card. NOTHING is chosen for the
 *                   player: with the choice open «جمع کن» waits (greyed) and, the pref off, a second
 *                   tap on the card only nudges it. A lone set starts chosen.
 *   A SECOND TAP    on the lifted card with «تأیید قبل از بازی کارت» ON ALWAYS puts it back
 *                   (`retapOf`) — every card kind, chosen or not: the setting exists so a double tap
 *                   plays nothing (its own rule in `lift-to-play.ts`; controller's ruling of
 *                   2026-10-10 over R4's «or on the same card»). The put-back drops the choice with
 *                   the lift, as a switch and a new frame do: the re-lifted card asks again.
 *   A Jack          one set — every number card and Jack on the table — chosen from the start.
 *   FORCED CAPTURE  a card with any set never trails: no move here ever has an empty capture for
 *                   it, and the empty felt never plays a card whose choice is open.
 *
 * Every answer is derived from the frame's `legal`: a new frame, a refused move or a switch to
 * another card drops a choice by itself (`derivePicker` ignores a pick for another card, another
 * frame, or a set the card no longer has) — the same rule the lift itself lives by.
 * ------------------------------------------------------------------ */

/** The table cards one capture takes — the server's own array, sent back as it came. */
export type CaptureSet = readonly string[];

/** Each playable hand card → its capture sets, in the server's order. An EMPTY list is a card that
 *  only trails; a card absent from the map has no legal move (not your turn, or not playable). */
export type CaptureOptions = ReadonlyMap<string, readonly CaptureSet[]>;

/** The server's legal list, grouped by the hand card each move plays. A card's moves are all
 *  captures or one trail (the engine forces a capture), so a trail reads as «no set». */
export function optionsByCard(legal: readonly PasurMove[]): CaptureOptions {
  const map = new Map<string, CaptureSet[]>();
  for (const move of legal) {
    const sets = map.get(move.card);
    const set = move.capture;
    if (!sets) map.set(move.card, set.length > 0 ? [set] : []);
    else if (set.length > 0) sets.push(set);
  }
  return map;
}

/** A set's identity, order-insensitive — the engine's own key. */
export const optionKey = captureKey;

/**
 * The cards a tap LIFTS rather than plays. With «تأیید قبل از بازی کارت» on, every playable card (the
 * lift is the confirmation); off, only a card with SEVERAL sets — the one choice a single tap cannot
 * make for the player (R4's accessibility clause), while a trail or a lone capture plays at once.
 */
export function liftableCards(options: CaptureOptions, liftToPlay: boolean): ReadonlySet<string> {
  const out = new Set<string>();
  for (const [card, sets] of options) if (liftToPlay || sets.length > 1) out.add(card);
  return out;
}

/** The set `key` names among `sets`, or undefined. */
function setByKey(sets: readonly CaptureSet[], key: string | null): CaptureSet | undefined {
  if (key === null) return undefined;
  return sets.find((s) => captureKey(s) === key);
}

/**
 * What a send of `card` IS: its trail (no set), its one capture (a lone set), or the set
 * `selectedKey` names — and NOTHING while the choice is open (several sets, none chosen), or for a
 * card the server does not list. A card with sets never trails from here.
 */
export function moveFor(
  options: CaptureOptions,
  card: string,
  selectedKey: string | null,
): PasurMove | null {
  const sets = options.get(card);
  if (!sets) return null;
  if (sets.length === 0) return { card, capture: [] };
  const set = sets.length === 1 ? sets[0] : setByKey(sets, selectedKey);
  return set ? { card, capture: set } : null;
}

/** What a SECOND tap on the lifted card does. With «تأیید قبل از بازی کارت» ON it ALWAYS puts the
 *  card back — the setting's own rule («tap it again to put it back»; a double tap plays nothing),
 *  the felt or «جمع کن» confirms. With the setting OFF only a card with several sets is ever lifted,
 *  and its second tap is R4's confirm: a settled choice PLAYS, an open one only NUDGES the card —
 *  nothing is chosen for the player; a trail-only card goes back down (حکم's rule; off, it is never
 *  lifted — kept for the record). A card the server does not list was never lifted: put back. */
export function retapOf(
  options: CaptureOptions,
  card: string,
  selectedKey: string | null,
  liftToPlay: boolean,
): "putBack" | "play" | "nudge" {
  if (liftToPlay) return "putBack";
  const sets = options.get(card);
  if (!sets || sets.length === 0) return "putBack";
  return moveFor(options, card, selectedKey) ? "play" : "nudge";
}

/** The choice the player made: which set of which lifted card, in which server frame. */
export interface Pick {
  readonly card: string;
  readonly key: string;
  readonly frame: number;
}

/** What the table shows for the lift and the choice as they stand. */
export interface Picker {
  /** The lifted hand card the server still lists, or null. */
  readonly lifted: string | null;
  /** Its capture sets ([] for a trail-only card, or nothing lifted). */
  readonly sets: readonly CaptureSet[];
  /** The chosen set's key — a lone set from the start — or null while the choice is open. */
  readonly selectedKey: string | null;
  /** ELIGIBLE: every table card in any set — the soft glow. */
  readonly eligible: ReadonlySet<string>;
  /** CHOSEN: the selected set's cards — the strong ring and the count badge. */
  readonly chosen: ReadonlySet<string>;
  /** What a confirm sends: the trail, the lone capture, the chosen set — null while open. */
  readonly move: PasurMove | null;
  /** The prompt at the felt's bottom: the generic one, «which set?», or none (the pill says it). */
  readonly prompt: "play" | "pick" | null;
  /** The «جمع کن» pill: ready, waiting (greyed: nothing chosen yet), or absent (no capture lifted). */
  readonly confirm: "ready" | "waiting" | null;
}

/**
 * The picker, derived: `lifted` is the shared lift's answer (already dropped on a new frame or when
 * the card stopped being playable); `pick` counts only for THAT card, in THIS frame, naming a set
 * the card still has. A lone set is chosen without a pick.
 */
export function derivePicker(
  lifted: string | null,
  pick: Pick | null,
  options: CaptureOptions,
  frameKey: number,
): Picker {
  const sets = lifted === null ? undefined : options.get(lifted);
  if (lifted === null || !sets) {
    return {
      lifted: null,
      sets: [],
      selectedKey: null,
      eligible: new Set(),
      chosen: new Set(),
      move: null,
      prompt: "play",
      confirm: null,
    };
  }
  const picked =
    pick && pick.card === lifted && pick.frame === frameKey ? setByKey(sets, pick.key) : undefined;
  const selected = sets.length === 1 ? sets[0] : picked;
  const selectedKey = selected ? captureKey(selected) : null;
  const eligible = new Set<string>();
  for (const s of sets) for (const c of s) eligible.add(c);
  const chosen = new Set<string>(selected ?? []);
  const move = moveFor(options, lifted, selectedKey);
  return {
    lifted,
    sets,
    selectedKey,
    eligible,
    chosen,
    move,
    prompt: sets.length === 0 ? "play" : selected ? null : "pick",
    confirm: sets.length === 0 ? null : selected ? "ready" : "waiting",
  };
}

/**
 * A tap on a table card: the key of the set it chooses — the first set holding the card, or, when
 * the chosen set already holds it, the NEXT one holding it (wrapping round: repeated taps cycle
 * through them) — or null when nothing is lifted or no set holds the card (an ineligible card, the
 * empty felt's twin).
 */
export function pickOnPool(picker: Picker, tableCard: string): string | null {
  if (picker.lifted === null) return null;
  const holding = picker.sets.filter((s) => s.includes(tableCard));
  if (holding.length === 0) return null;
  const at = holding.findIndex((s) => captureKey(s) === picker.selectedKey);
  const next = at < 0 ? holding[0] : holding[(at + 1) % holding.length];
  return next ? captureKey(next) : null;
}

/**
 * Does a tap on the EMPTY felt play the lifted card? Only under «تأیید قبل از بازی کارت» (the felt
 * tap is that setting's confirmation, as on حکم and بی‌دل), and only for a card with ONE legal move:
 * its trail, or its lone capture. A card whose choice is open — or already made among several sets
 * — is confirmed on «جمع کن» (with the setting ON a retap puts the card back), never by the felt
 * (R4): forced capture holds, and the felt never «drops» a card that can capture.
 */
export function feltPlays(
  options: CaptureOptions,
  lifted: string | null,
  liftToPlay: boolean,
): boolean {
  if (!liftToPlay || lifted === null) return false;
  const sets = options.get(lifted);
  return sets !== undefined && sets.length <= 1;
}
